/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';

import { useShareStore } from '@stores/share-store';
import { TenantType } from '@api/types';
import { LOCAL_STORAGE_KEYS } from '@constants/local-storage-constants';
import { isAfterToday, isBeforeToday } from '@scripts/utils/date-utils';

type TenantStoreStateType = {
  data: TenantType;
  initialized: boolean;
};

const initialState = (): TenantStoreStateType => ({
  data: {
    id: null,
    name: '',
    domain: localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT_DOMAIN) || '',
    accountId: '',
    description: '',
    licenseCount: 0,
    subscriptionUid: null,
    subscribedAt: null,
    expiresAt: null,
    emailPolicyConfig: null,
    blacklistedEmailDomains: [],
    whitelistedEmailDomains: [],
    whitelistedEmails: [],
  },
  initialized: false,
});

export const useTenantStore = defineStore({
  id: 'tenant',
  state: initialState,
  getters: {
    domain: (state) => state.data.domain || '',
    isEnterprise: (state) => {
      if (state.data.subscriptionUid === null) {
        return false;
      }

      const shareStore = useShareStore();
      if (!shareStore.initialized) {
        throw new Error('Shares are not initialized.');
      }

      const isEnterprise = (
        state.data.subscriptionUid === shareStore.enterpriseSubscription?.uid);
      const hasStarted = (
        state.data.subscribedAt === null || isBeforeToday(state.data.subscribedAt));
      const notExpired = (
        state.data.expiresAt === null || isAfterToday(state.data.expiresAt));

      return isEnterprise && hasStarted && notExpired;
    },
    isSelected: (state) => !!state.data.domain,
  },
  actions: {
    $reset() {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT_DOMAIN);
      Object.assign(this, initialState());
    },
    set(data: TenantType): void {
      Object.keys(data).forEach((key) => {
        this.data[key] = data[key];
      });
      if (data.domain) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_DOMAIN, data.domain);
        this.initialized = true;
      }
    },
    setDomain(domain: string): void {
      this.data.domain = domain;
      localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_DOMAIN, domain);
    },
  },
});
